import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LayoutService } from '../../services/layout.service';

interface AtalhoAtendimento {
  rota: string;
  rotulo: string;
  icone: string;
}

/**
 * Botão suspenso de navegação rápida no celular.
 *
 * <p>No celular, trocar de tela custava abrir o menu lateral e descer dois níveis, com o aparelho
 * na mão. Aqui é um toque para abrir e outro para ir.</p>
 *
 * <p>O conjunto de atalhos segue o perfil da empresa: quem atende no salão recebe mapa de PA e
 * preparo (KDS); quem não atende recebe as telas do dia a dia do ERP — vendas, lançamento, cadastro
 * e produto. Antes, a empresa sem salão simplesmente não via botão nenhum, o que desperdiçava o
 * atalho justamente para a maioria dos clientes.</p>
 *
 * <p>Aparece <b>só em tela estreita</b>: no desktop o menu lateral já está visível o tempo todo e um
 * botão flutuante seria ruído em cima do conteúdo. E some da tela em que já se está, para não
 * oferecer ir para onde o usuário já está.</p>
 */
@Component({
  selector: 'app-atendimento-fab',
  templateUrl: './atendimento-fab.component.html',
  styleUrls: ['./atendimento-fab.component.scss']
})
export class AtendimentoFabComponent implements OnInit, OnDestroy {

  /** Força a rota "atual" quando usado dentro de uma tela; vazio deixa o roteador decidir. */
  @Input() atual = '';

  aberto = false;
  estreito = window.innerWidth < 768;
  /** Empresa marcada como ponto de atendimento — é o que decide QUAL conjunto de atalhos aparece. */
  kdsHabilitado = false;

  /** URL corrente, para esconder o atalho da tela em que já se está. */
  private urlAtual = '';
  private navegacao?: Subscription;

  /**
   * Destinos que não têm outro caminho curto no celular: mapa, preparo, comanda de venda direta e
   * vendas. A comanda de mesa e o delivery ficam de fora porque se chega neles PELO mapa.
   */
  private readonly atalhosSalao: AtalhoAtendimento[] = [
    { rota: '/fat_movto/mapa-pa', rotulo: 'Mapa de Atendimento', icone: 'grid_view' },
    { rota: '/fat_movto/comanda', rotulo: 'Comanda Venda Direta', icone: 'receipt_long' },
    { rota: '/fat_movto/venda', rotulo: 'Vendas', icone: 'point_of_sale' },
    { rota: '/infokaw-food/kds', rotulo: 'Preparo (KDS)', icone: 'soup_kitchen' }
  ];

  /**
   * Empresa que não atende no salão (pacotes fiscal/básico): as quatro telas do uso diário.
   *
   * <p>Todas existem em qualquer pacote do ERP — por isso a lista é fixa. Filtrar por plano de
   * assinatura não é possível hoje: a sessão não carrega o plano, só os perfis de empresa.</p>
   */
  private readonly atalhosErp: AtalhoAtendimento[] = [
    { rota: '/fat_movto/venda', rotulo: 'Vendas', icone: 'point_of_sale' },
    { rota: '/fat_movto/lancamentos', rotulo: 'Lançamentos', icone: 'receipt_long' },
    { rota: '/cad_cadastros/cadastros', rotulo: 'Cadastros', icone: 'people' },
    { rota: '/cad_faturamento/produtos', rotulo: 'Produtos', icone: 'inventory_2' }
  ];

  constructor(private router: Router, private layout: LayoutService) { }

  ngOnInit(): void {
    // regra única, compartilhada com os atalhos do dashboard
    this.kdsHabilitado = this.layout.empresaAtendeNoSalao();

    // Vive no layout, então acompanha a navegação em vez de receber a rota de cada tela.
    this.urlAtual = this.router.url || '';
    this.navegacao = this.router.events
      .pipe(filter(evento => evento instanceof NavigationEnd))
      .subscribe((evento: any) => {
        this.urlAtual = evento?.urlAfterRedirects || evento?.url || '';
        this.aberto = false;
      });
  }

  ngOnDestroy(): void {
    this.navegacao?.unsubscribe();
  }


  @HostListener('window:resize')
  aoRedimensionar(): void {
    this.estreito = window.innerWidth < 768;
    if (!this.estreito) {
      this.aberto = false;
    }
  }

  get opcoes(): AtalhoAtendimento[] {
    // Um conjunto OU o outro, nunca os dois: misturar salão e ERP encheria o botão de opções que
    // aquela empresa não usa, que é o oposto do atalho.
    const atalhos = this.kdsHabilitado ? this.atalhosSalao : this.atalhosErp;

    // esconde o atalho da tela em que já se está.
    return atalhos
      .filter(a => this.atual ? !a.rota.endsWith(this.atual) : !this.urlAtual.startsWith(a.rota));
  }

  alternar(): void {
    this.aberto = !this.aberto;
  }

  ir(atalho: AtalhoAtendimento): void {
    this.aberto = false;
    this.router.navigateByUrl(atalho.rota);
  }

}
